import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Conference performance is derived from real CRM data, never hand-entered.
 *
 * Attribution: LeadOverview writes the selected conference's *name* straight into
 * `leadSource` (see LeadOverview.js — the conference dropdown uses conference.name as
 * the option value), and that value survives lead -> client conversion because the
 * account document is mutated in place. So an account belongs to a conference when its
 * source string matches that conference's name.
 *
 * Conferences live in `conferences` (what ConferencesReports writes) and, for older
 * records, `conferences-reports` (what the lead-source dropdown reads first). Both are
 * read and de-duped by name so attribution can't miss an event; each row remembers the
 * collection it came from so edits and deletes target the right document.
 */

export const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value.toDate === 'function') return value.toDate();
  if (value.seconds) return new Date(value.seconds * 1000);
  if (typeof value === 'string') {
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

export const toNumber = (value) => {
  const n = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(n) ? n : 0;
};

export const normalizeName = (value) => (value || '').toString().trim().toLowerCase();

export const formatMoney = (value) => `$${Math.round(toNumber(value)).toLocaleString()}`;

// Where a lead came from. leadSource is authoritative; the rest are legacy shapes.
export const getAccountSource = (account) =>
  (account.leadSource || account.source || account.channel || account.campaign || '').toString().trim();

// Converted accounts flip type to 'Clients'; some legacy docs only carry result: 'Won'.
export const isClientAccount = (account) => {
  const type = (account.type || '').toString().toLowerCase();
  if (type === 'clients') return true;
  return (account.result || account.Result || '').toString().toLowerCase() === 'won';
};

export const isLostAccount = (account) =>
  (account.result || account.Result || '').toString().toLowerCase() === 'lost';

// program.monthlyAmount is the CRM-maintained MRR and beats a stale Stripe sync
export const getAccountMrr = (account) =>
  toNumber(account.program?.monthlyAmount) || toNumber(account.billing?.monthlyAmount) || 0;

// Purchases that count as revenue — drafts and cancellations do not
const isCountedPurchase = (purchase) => {
  const status = (purchase.status || '').toString().toLowerCase();
  return status !== 'cancelled' && status !== 'draft';
};

export const getPeriodStart = (timeFilter) => {
  if (!timeFilter || timeFilter === 'All Time') return null;
  const now = new Date();
  const start = new Date();
  if (timeFilter === 'This Year') {
    start.setMonth(0, 1);
  } else if (timeFilter === 'This Quarter') {
    start.setMonth(Math.floor(now.getMonth() / 3) * 3, 1);
  } else if (timeFilter === 'This Month') {
    start.setDate(1);
  } else {
    return null;
  }
  start.setHours(0, 0, 0, 0);
  return start;
};

const readConferenceCollection = async (collectionName) => {
  try {
    const snapshot = await getDocs(collection(db, collectionName));
    return snapshot.docs.map(docSnap => {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        collectionName,
        name: data.name || data.title || docSnap.id,
        startDate: toDate(data.startDate) || toDate(data.date),
        endDate: toDate(data.endDate),
      };
    });
  } catch (error) {
    console.error(`Error fetching ${collectionName}:`, error);
    return [];
  }
};

export const fetchConferences = async () => {
  const [primary, legacy] = await Promise.all([
    readConferenceCollection('conferences'),
    readConferenceCollection('conferences-reports'),
  ]);
  const seen = new Set();
  const merged = [];
  [...primary, ...legacy].forEach(conference => {
    const key = normalizeName(conference.name);
    if (!key || seen.has(key)) return;
    seen.add(key);
    merged.push(conference);
  });
  return merged.sort((a, b) => (b.startDate?.getTime() || 0) - (a.startDate?.getTime() || 0));
};

/**
 * Every account that could be attributed to a conference, plus the purchases that
 * turn those accounts into revenue. Clients are included alongside leads because a
 * converted lead becomes type 'Clients' on the same document.
 */
export const fetchAttributionData = async () => {
  const [accountsSnapshot, purchasesSnapshot] = await Promise.all([
    // Casing varies in older documents, so match every variant rather than lose accounts
    getDocs(query(collection(db, 'accounts'), where('type', 'in', ['Leads', 'Clients', 'leads', 'clients', 'Lead', 'Client']))),
    getDocs(collection(db, 'clientpurchases')),
  ]);

  const accounts = accountsSnapshot.docs.map(docSnap => ({
    id: docSnap.id,
    ...docSnap.data(),
    createdAt: toDate(docSnap.data().createdAt),
    receivedDate: toDate(docSnap.data().receivedDate),
    contractStart: toDate(docSnap.data().contractStart),
  }));

  const purchases = purchasesSnapshot.docs.map(docSnap => ({
    id: docSnap.id,
    ...docSnap.data(),
    startDate: toDate(docSnap.data().startDate),
    date: toDate(docSnap.data().date),
    createdAt: toDate(docSnap.data().createdAt),
  }));

  return { accounts, purchases };
};

// accountId -> revenue actually booked against that account
export const buildRevenueByAccount = (purchases = []) => {
  const totals = new Map();
  purchases.filter(isCountedPurchase).forEach(purchase => {
    const accountId = purchase.accountId;
    if (!accountId) return;
    totals.set(accountId, (totals.get(accountId) || 0) + toNumber(purchase.total));
  });
  return totals;
};

// Booked purchases win; lifetimePaid from the Stripe sync covers accounts with none
export const getAccountRevenue = (account, revenueByAccount) => {
  const booked = revenueByAccount?.get(account.id) || 0;
  if (booked > 0) return booked;
  return toNumber(account.billing?.lifetimePaid) || toNumber(account.billing?.lifetimeAmount) || 0;
};

const roiPercent = (revenue, cost) =>
  cost > 0 ? Math.round(((revenue - cost) / cost) * 1000) / 10 : null;

const perUnit = (cost, units) =>
  cost > 0 && units > 0 ? Math.round((cost / units) * 100) / 100 : null;

/**
 * Rolls accounts and purchases up per conference.
 *
 * @param conferences  merged conference docs (from fetchConferences)
 * @param accounts     leads + clients (from fetchAttributionData)
 * @param purchases    clientpurchases (from fetchAttributionData)
 * @param periodStart  only include conferences held on/after this date (null = all time)
 * @returns { rows, totals, unspecified }
 */
export const buildConferenceReport = ({ conferences = [], accounts = [], purchases = [], periodStart = null }) => {
  const inRange = conferences.filter(conference =>
    !periodStart || (conference.startDate instanceof Date && conference.startDate >= periodStart)
  );

  const revenueByAccount = buildRevenueByAccount(purchases);
  const emptyStats = () => ({ leads: 0, clients: 0, lost: 0, revenue: 0, mrr: 0 });

  const attributed = new Map();
  inRange.forEach(conference => attributed.set(normalizeName(conference.name), emptyStats()));
  const unspecified = emptyStats();

  accounts.forEach(account => {
    const source = normalizeName(getAccountSource(account));
    if (!source) return;
    // 'Conference' with no event picked — real conference leads we can't tie to an event
    const stats = source === 'conference' ? unspecified : attributed.get(source);
    if (!stats) return;

    stats.leads += 1;
    if (isClientAccount(account)) {
      stats.clients += 1;
      stats.mrr += getAccountMrr(account);
    } else if (isLostAccount(account)) {
      stats.lost += 1;
    }
    stats.revenue += getAccountRevenue(account, revenueByAccount);
  });

  const rows = inRange.map(conference => {
    const stats = attributed.get(normalizeName(conference.name)) || emptyStats();
    const cost = toNumber(conference.cost);
    return {
      id: conference.id,
      key: conference.id,
      collectionName: conference.collectionName,
      name: conference.name,
      location: conference.location || '',
      type: conference.type || '',
      date: conference.startDate || null,
      endDate: conference.endDate || null,
      dateLabel: conference.startDate
        ? conference.startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        : '-',
      leads: stats.leads,
      clients: stats.clients,
      lost: stats.lost,
      revenue: stats.revenue,
      mrr: stats.mrr,
      cost,
      roi: roiPercent(stats.revenue, cost),
      costPerLead: perUnit(cost, stats.leads),
      costPerClient: perUnit(cost, stats.clients),
      conversionRate: stats.leads > 0 ? Math.round((stats.clients / stats.leads) * 1000) / 10 : 0,
      // Legacy hand-entered numbers, kept only so a mismatch stays visible
      recordedLeads: toNumber(conference.leadsGenerated),
      recordedClients: toNumber(conference.clientsAcquired),
      recordedRevenue: toNumber(conference.revenue),
    };
  }).sort((a, b) => (b.leads - a.leads) || ((b.date?.getTime() || 0) - (a.date?.getTime() || 0)));

  const sum = (key) => rows.reduce((total, row) => total + row[key], 0);
  const cost = sum('cost');
  const revenue = sum('revenue') + unspecified.revenue;
  const leads = sum('leads') + unspecified.leads;
  const clients = sum('clients') + unspecified.clients;

  return {
    rows,
    unspecified,
    totals: {
      conferences: rows.length,
      leads,
      clients,
      lost: sum('lost') + unspecified.lost,
      revenue,
      mrr: sum('mrr') + unspecified.mrr,
      cost,
      roi: roiPercent(revenue, cost),
      costPerLead: perUnit(cost, leads),
      costPerClient: perUnit(cost, clients),
      conversionRate: leads > 0 ? Math.round((clients / leads) * 1000) / 10 : 0,
    },
  };
};
